/**
 * Enhanced Data Categories Service
 * Service for managing enhanced data categories with multi-entry support
 */

import { supabase } from '@xanectech/supabase-client';
import { createLogger } from '@xanectech/lib';
import {
  encryptValue,
  hashValue,
  shouldEncryptField,
} from '../utils/personal-data/encryption';
import type {
  Database,
  DataCategory,
  DataCategoryInsert,
  DataCategoryUpdate,
  FieldDefinition,
  CategoryEntry,
  CategoryEntryInsert,
  CategoryEntryUpdate,
  CategoryFieldData,
  CategoryFieldDataInsert,
  CategoryFieldDataUpdate,
  CategoryCompleteness,
  CategoryValidationResult,
} from '@xanectech/types';

const logger = createLogger('Data - EnhancedDataCategoryService');

type SearchUserDataResult = Record<string, unknown>;

type UserFieldDataResult = CategoryFieldData & {
  field_definition: FieldDefinition | null;
};

type DataCategoryRow = Database['public']['Tables']['data_categories']['Row'];

function normalizeDataCategory(category: DataCategoryRow): DataCategory {
  return {
    ...category,
    allows_multiple_entries: category.allows_multiple_entries ?? false,
    color_theme: category.color_theme ?? '#6366f1',
    created_at: category.created_at ?? new Date().toISOString(),
    display_order: category.display_order ?? 0,
    icon: category.icon ?? 'Folder',
    is_active: category.is_active ?? true,
    is_mandatory: category.is_mandatory ?? false,
    is_system_category: category.is_system_category ?? false,
    max_entries: category.max_entries ?? undefined,
    type_field_id: category.type_field_id ?? undefined,
    updated_at: category.updated_at ?? new Date().toISOString(),
  };
}

export class EnhancedDataCategoryService {
  /**
   * Get all active data categories
   */
  static async getCategories(): Promise<DataCategory[]> {
    const { data, error } = await supabase
      .from('data_categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return (data || []).map((category) =>
      normalizeDataCategory(category as DataCategoryRow),
    );
  }

  /**
   * Get all data categories for a specific user, including entry counts
   */
  static async getCategoriesWithEntryCounts(
    userId: string,
  ): Promise<(DataCategory & { entry_count: number })[]> {
    const { data: categories, error: categoriesError } = await supabase
      .from('data_categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (categoriesError) throw categoriesError;

    const { data: entries, error: entriesError } = await supabase
      .from('category_entries')
      .select('category_id')
      .eq('user_id', userId);

    if (entriesError) throw entriesError;

    const entryCountMap = new Map<string, number>();
    for (const entry of entries || []) {
      if (!entry.category_id) continue;
      entryCountMap.set(
        entry.category_id,
        (entryCountMap.get(entry.category_id) || 0) + 1,
      );
    }

    return (categories || []).map((category) => {
      const normalizedCategory = normalizeDataCategory(
        category as DataCategoryRow,
      );
      return {
        ...normalizedCategory,
        entry_count: entryCountMap.get(category.id) || 0,
      };
    });
  }

  /**
   * Get category by ID
   */
  static async getCategory(categoryId: string): Promise<DataCategory | null> {
    const { data, error } = await supabase
      .from('data_categories')
      .select('*')
      .eq('id', categoryId)
      .eq('is_active', true)
      .maybeSingle();

    if (error) throw error;
    return data ? normalizeDataCategory(data as DataCategoryRow) : null;
  }

  /**
   * Create new category (admin only)
   */
  static async createCategory(
    categoryData: DataCategoryInsert,
  ): Promise<DataCategory> {
    const { data, error } = await supabase
      .from('data_categories')
      .insert(categoryData)
      .select()
      .single();

    if (error) throw error;
    return normalizeDataCategory(data as DataCategoryRow);
  }

  /**
   * Update category (admin only)
   */
  static async updateCategory(
    id: string,
    updates: DataCategoryUpdate,
  ): Promise<DataCategory> {
    const { data, error } = await supabase
      .from('data_categories')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return normalizeDataCategory(data as DataCategoryRow);
  }

  /**
   * Get field definitions for a category
   */
  static async getCategoryFields(
    categoryId: string,
  ): Promise<FieldDefinition[]> {
    const { data, error } = await supabase
      .from('field_definitions')
      .select('*')
      .eq('category_id', categoryId)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) throw error;

    // Map Json types to proper TypeScript types
    return (data || []).map((field) => ({
      ...field,
      options: (field.options || {}) as Record<string, unknown>,
      country_context_rules: (field.country_context_rules || {}) as Record<
        string,
        unknown
      >,
      dependency_type: field.dependency_type as
        | 'conditional'
        | 'geographic_cascade'
        | null
        | undefined,
    })) as FieldDefinition[];
  }

  /**
   * Get user's entries for a category
   */
  static async getUserCategoryEntries(
    userId: string,
    categoryId: string,
  ): Promise<CategoryEntry[]> {
    const { data, error } = await supabase
      .from('category_entries')
      .select('*')
      .eq('user_id', userId)
      .eq('category_id', categoryId)
      .order('is_primary', { ascending: false })
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data;
  }

  /**
   * Get all user's entries across all categories
   */
  static async getAllUserEntries(userId: string): Promise<CategoryEntry[]> {
    const { data, error } = await supabase
      .from('category_entries')
      .select('*')
      .eq('user_id', userId)
      .order('is_primary', { ascending: false })
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data;
  }

  /**
   * Create new category entry for user
   */
  static async createCategoryEntry(
    entryData: CategoryEntryInsert,
  ): Promise<CategoryEntry> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('category_entries')
      .insert({
        ...entryData,
        user_id: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Update category entry
   */
  static async updateCategoryEntry(
    entryId: string,
    updates: CategoryEntryUpdate,
  ): Promise<CategoryEntry> {
    const { data, error } = await supabase
      .from('category_entries')
      .update(updates)
      .eq('id', entryId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Delete category entry
   */
  static async deleteCategoryEntry(entryId: string): Promise<void> {
    const { error } = await supabase
      .from('category_entries')
      .delete()
      .eq('id', entryId);

    if (error) throw error;
  }

  /**
   * Get field data for an entry
   */
  static async getEntryFieldData(
    entryId: string,
  ): Promise<CategoryFieldData[]> {
    const { data, error } = await supabase
      .from('category_field_data')
      .select('*')
      .eq('entry_id', entryId);

    if (error) throw error;
    return data;
  }

  /**
   * Set field data for an entry
   */
  static async setEntryFieldData(
    entryId: string,
    fieldId: string,
    value: string,
  ): Promise<CategoryFieldData> {
    // Get field definition to determine if encryption is needed
    const { data: fieldDef, error: fieldError } = await supabase
      .from('field_definitions')
      .select('data_type, is_sensitive, is_pii')
      .eq('id', fieldId)
      .single();

    if (fieldError) {
      logger.error('Error fetching field definition:', fieldError);
      throw fieldError;
    }

    // Determine if field should be encrypted
    const shouldEncrypt = shouldEncryptField({
      is_sensitive: fieldDef.is_sensitive || false,
      is_pii: fieldDef.is_pii || false,
      data_type: fieldDef.data_type,
    });

    // Encrypt the value if needed
    const encryptedValue = shouldEncrypt ? await encryptValue(value) : value;
    const valueHash = shouldEncrypt ? await hashValue(value) : null;

    const fieldDataInsert = {
      entry_id: entryId,
      field_id: fieldId,
      value_encrypted: encryptedValue,
      value_hash: valueHash,
      is_verified: false,
      verified_at: null,
      verified_by: null,
    } as const;

    const { data, error } = await supabase
      .from('category_field_data')
      .upsert(fieldDataInsert, {
        onConflict: 'entry_id,field_id',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Set multiple field data values for an entry
   */
  static async setEntryFieldDataBulk(
    entryId: string,
    fieldData: Record<string, string>,
  ): Promise<CategoryFieldData[]> {
    // Get field definitions for all fields
    const fieldIds = Object.keys(fieldData);
    const { data: fieldDefs, error: fieldError } = await supabase
      .from('field_definitions')
      .select('id, data_type, is_sensitive, is_pii')
      .in('id', fieldIds);

    if (fieldError) {
      logger.error('Error fetching field definitions:', fieldError);
      throw fieldError;
    }

    // Process each field with proper encryption
    const inserts = await Promise.all(
      Object.entries(fieldData).map(async ([fieldId, value]) => {
        const fieldDef = fieldDefs?.find((f) => f.id === fieldId);

        if (!fieldDef) {
          logger.warn(`Field definition not found for field_id: ${fieldId}`);
          // Default to encrypting unknown fields for security
          const encryptedValue = await encryptValue(value);
          const valueHash = await hashValue(value);
          return {
            entry_id: entryId,
            field_id: fieldId,
            value_encrypted: encryptedValue,
            value_hash: valueHash,
            is_verified: false,
            verified_at: null,
            verified_by: null,
          };
        }

        // Determine if field should be encrypted
        const shouldEncrypt = shouldEncryptField({
          is_sensitive: fieldDef.is_sensitive || false,
          is_pii: fieldDef.is_pii || false,
          data_type: fieldDef.data_type,
        });

        // Encrypt the value if needed
        const encryptedValue = shouldEncrypt
          ? await encryptValue(value)
          : value;
        const valueHash = shouldEncrypt ? await hashValue(value) : null;

        return {
          entry_id: entryId,
          field_id: fieldId,
          value_encrypted: encryptedValue,
          value_hash: valueHash,
          is_verified: false,
          verified_at: null,
          verified_by: null,
        };
      }),
    );

    const { data, error } = await supabase
      .from('category_field_data')
      .upsert(inserts, {
        onConflict: 'entry_id,field_id',
      })
      .select();

    if (error) throw error;
    return data;
  }

  /**
   * Get category completeness for user
   */
  static async getCategoryCompleteness(
    userId: string,
    categoryId: string,
  ): Promise<CategoryCompleteness> {
    const { data, error } = await supabase.rpc('get_category_completeness', {
      p_user_id: userId,
      p_category_id: categoryId,
    });

    if (error) throw error;
    return (
      data[0] || {
        total_required_fields: 0,
        completed_fields: 0,
        completeness_percentage: 0,
      }
    );
  }

  /**
   * Validate field data
   */
  static async validateFieldData(
    fieldId: string,
    value: string,
    countryCode?: string,
  ): Promise<CategoryValidationResult> {
    const { data, error } = await supabase.rpc('validate_field_data', {
      p_field_id: fieldId,
      p_value: value,
      p_country_code: countryCode,
    });

    if (error) throw error;
    const fallback: CategoryValidationResult = {
      is_valid: false,
      validation_errors: ['Validation failed'],
    };

    return (data?.[0] as CategoryValidationResult | undefined) || fallback;
  }

  /**
   * Search user's data across categories
   */
  static async searchUserData(
    userId: string,
    searchTerm: string,
    categoryIds?: string[],
  ): Promise<SearchUserDataResult[]> {
    const { data, error } = await supabase.rpc('search_user_data', {
      p_user_id: userId,
      p_search_term: searchTerm,
      p_category_ids: categoryIds,
    });

    if (error) throw error;
    return (data as SearchUserDataResult[] | null) || [];
  }

  /**
   * Create default category entries for user
   */
  static async createDefaultEntries(userId: string): Promise<void> {
    const { error } = await supabase.rpc('create_default_category_entries', {
      p_user_id: userId,
    });

    if (error) throw error;
  }

  /**
   * Get comprehensive entry data with field values
   */
  static async getEntryWithFieldData(entryId: string) {
    // Get the entry
    const { data: entry, error: entryError } = await supabase
      .from('category_entries')
      .select(
        `
        *,
        category:data_categories(*)
      `,
      )
      .eq('id', entryId)
      .single();

    if (entryError) throw entryError;

    // Get field definitions for the category
    const { data: fields, error: fieldsError } = await supabase
      .from('field_definitions')
      .select('*')
      .eq('category_id', entry.category_id)
      .eq('is_active', true)
      .order('display_order');

    if (fieldsError) throw fieldsError;

    // Get field data for the entry
    const { data: fieldData, error: fieldDataError } = await supabase
      .from('category_field_data')
      .select('*')
      .eq('entry_id', entryId);

    if (fieldDataError) throw fieldDataError;

    // Combine field definitions with data
    const fieldDataMap = new Map<string, CategoryFieldData>(
      ((fieldData || []) as CategoryFieldData[]).map(
        (fd) => [fd.field_id, fd] as const,
      ),
    );
    const fieldsWithData = fields.map((field) => ({
      ...field,
      data: fieldDataMap.get(field.id as string) || null,
      value: fieldDataMap.get(field.id as string)?.value_encrypted || null,
    }));

    return {
      entry,
      fields: fieldsWithData,
      field_data: fieldData,
      category: entry.category,
    };
  }

  /**
   * Create field data for an entry
   */
  static async createFieldData(
    fieldData: CategoryFieldDataInsert,
  ): Promise<CategoryFieldData> {
    const { data, error } = await supabase
      .from('category_field_data')
      .insert(fieldData)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Update field data
   */
  static async updateFieldData(
    fieldDataId: string,
    updates: CategoryFieldDataUpdate,
  ): Promise<CategoryFieldData> {
    const { data, error } = await supabase
      .from('category_field_data')
      .update(updates)
      .eq('id', fieldDataId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Get all field data for user's entries across all categories
   * Used for search functionality
   */
  static async getAllUserFieldData(
    userId: string,
  ): Promise<UserFieldDataResult[]> {
    try {
      const { data, error } = await supabase
        .from('category_field_data')
        .select(
          `*,
          category_entries!inner(user_id)`,
        )
        .eq('category_entries.user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Enhance with field definitions separately to avoid complex joins
      const enhancedData = await Promise.all(
        (data || []).map(async (fieldData) => {
          const { data: fieldDef } = await supabase
            .from('field_definitions')
            .select('*')
            .eq('id', fieldData.field_id)
            .single();

          return {
            ...fieldData,
            field_definition: fieldDef,
          };
        }),
      );

      return enhancedData as UserFieldDataResult[];
    } catch (error) {
      logger.error('Error fetching user field data:', {
        error: error instanceof Error ? error.message : String(error),
      });
      return [];
    }
  }
}
